// Streaming transport for the analytics batch endpoint.
//
// The tabs used to fan out into one request per section, which broke on ranges
// longer than the warmed 30 days: each query outlives Heroku's 30-second limit on
// the first byte, and the router's 503 carries no CORS headers, so the browser
// reported a CORS error and hid the real cause. One batch request instead streams
// NDJSON, so the first byte lands immediately and every section shows up the
// moment it is ready.
//
// Raw fetch rather than the shared axios instance: browser axios cannot read a
// response body as a stream (responseType "stream" is Node-only). Auth survives
// the switch because the JWT lives in an HttpOnly cookie the server reads itself —
// `credentials: "include"` sends it exactly as axios did. What does not survive is
// the 401 -> refresh -> retry interceptor, which is why that is handled here.
import { refreshAccessToken } from "./auth";
import {
  ActivityDashboardRequest,
  ActivityDashboardResponse,
  ClicksBusinessFunnelRequest,
  ClicksBusinessFunnelResponse,
  ClicksOverviewRequest,
  ClicksOverviewResponse,
  ClicksSeriesRequest,
  ClicksSeriesResponse,
  ClicksTrendingRequest,
  ClicksTrendingResponse,
  SearchFunnelOverviewRequest,
  SearchFunnelOverviewResponse,
  SearchFunnelSeriesRequest,
  SearchFunnelSeriesResponse,
  SearchQueryFunnelRequest,
  SearchQueryFunnelResponse,
  SearchTrendingRequest,
  SearchTrendingResponse,
  adaptActivityDashboard,
  adaptClicksBusinessFunnel,
  adaptClicksOverview,
  adaptClicksSeries,
  adaptClicksTrending,
  adaptSearchFunnelOverview,
  adaptSearchFunnelSeries,
  adaptSearchQueryFunnel,
  adaptSearchTrending,
} from "./analyticsAdmin";

const API_URL = process.env.REACT_APP_BACKEND_API_URL;

// The property name the batch request nests each query under. One per query type,
// matching AnalyticsBatchSection on the server.
export type SectionKind =
  | "clicksOverview"
  | "clicksSeries"
  | "clicksBusinessFunnel"
  | "clicksTrending"
  | "searchFunnelOverview"
  | "searchQueryFunnel"
  | "searchFunnelSeries"
  | "searchTrending"
  | "activityDashboard";

/**
 * One section to ask for. `adapt` is the same transform the single-endpoint
 * caller applies — several of them do more than fill in nulls — and `count` is
 * how many rows this shape holds, which is what the progress line reports. The
 * server never counts rows: doing so would mean teaching it the shape of every
 * response, and mirroring page knowledge server-side is what broke the cache
 * warm-ups twice.
 */
export interface SectionDescriptor<T> {
  kind: SectionKind;
  query: object;
  adapt: (raw: any) => T;
  count: (data: T) => number;
}

export const clicksOverview = (
  query: ClicksOverviewRequest
): SectionDescriptor<ClicksOverviewResponse> => ({
  kind: "clicksOverview",
  query,
  adapt: adaptClicksOverview,
  count: (d) => d.segments.length,
});

export const clicksSeries = (
  query: ClicksSeriesRequest
): SectionDescriptor<ClicksSeriesResponse> => ({
  kind: "clicksSeries",
  query,
  adapt: adaptClicksSeries,
  count: (d) => d.series.reduce((n, s) => n + s.points.length, 0),
});

export const clicksBusinessFunnel = (
  query: ClicksBusinessFunnelRequest
): SectionDescriptor<ClicksBusinessFunnelResponse> => ({
  kind: "clicksBusinessFunnel",
  query,
  adapt: adaptClicksBusinessFunnel,
  count: (d) => d.items.length,
});

export const clicksTrending = (
  query: ClicksTrendingRequest
): SectionDescriptor<ClicksTrendingResponse> => ({
  kind: "clicksTrending",
  query,
  adapt: adaptClicksTrending,
  count: (d) => d.buckets.reduce((n, b) => n + b.items.length, 0),
});

export const searchFunnelOverview = (
  query: SearchFunnelOverviewRequest
): SectionDescriptor<SearchFunnelOverviewResponse> => ({
  kind: "searchFunnelOverview",
  query,
  adapt: adaptSearchFunnelOverview,
  count: (d) => d.segments.length,
});

export const searchQueryFunnel = (
  query: SearchQueryFunnelRequest
): SectionDescriptor<SearchQueryFunnelResponse> => ({
  kind: "searchQueryFunnel",
  query,
  adapt: adaptSearchQueryFunnel,
  count: (d) => d.items.length,
});

export const searchFunnelSeries = (
  query: SearchFunnelSeriesRequest
): SectionDescriptor<SearchFunnelSeriesResponse> => ({
  kind: "searchFunnelSeries",
  query,
  adapt: adaptSearchFunnelSeries,
  count: (d) => d.series.reduce((n, s) => n + s.points.length, 0),
});

export const searchTrending = (
  query: SearchTrendingRequest
): SectionDescriptor<SearchTrendingResponse> => ({
  kind: "searchTrending",
  query,
  adapt: adaptSearchTrending,
  count: (d) => d.buckets.reduce((n, b) => n + b.items.length, 0),
});

export const activityDashboard = (
  query: ActivityDashboardRequest
): SectionDescriptor<ActivityDashboardResponse> => ({
  kind: "activityDashboard",
  query,
  adapt: adaptActivityDashboard,
  count: (d) => Object.values(d.series).reduce((n, s) => n + s.length, 0),
});

export type BatchFrame =
  | { t: "start"; total: number }
  | { t: "beat"; done: number; total: number; elapsedMs: number }
  | { t: "section"; id: string; ok: true; data: any; elapsedMs: number }
  | { t: "section"; id: string; ok: false; error: string; elapsedMs: number }
  | {
      t: "done";
      succeeded?: number;
      failed?: number;
      elapsedMs: number;
      pending?: string[];
      fatal?: string;
    };

/**
 * Split a chunk of the stream into whole lines, carrying any partial last line
 * over to the next chunk. A frame is not guaranteed to arrive in one piece — the
 * business funnel alone runs to a couple of hundred kilobytes — so the tail
 * buffer is what keeps a split frame from being dropped as unparseable.
 */
export const splitFrames = (
  buffered: string,
  chunk: string
): { lines: string[]; rest: string } => {
  const combined = buffered + chunk;
  const parts = combined.split("\n");
  const rest = parts.pop() ?? "";
  return { lines: parts.filter((line) => line.trim().length > 0), rest };
};

export interface BatchSectionRequest {
  id: string;
  descriptor: SectionDescriptor<any>;
}

/**
 * The wire body. Each section nests its query under the property name the
 * server's AnalyticsBatchSection declares for that query type — camelCased, which
 * is exactly what the backend's JSON binder expects. Exported so a test can pin
 * that mapping: a wrong key binds to null, the section fails validation, and the
 * only symptom is a section that never loads.
 */
export const buildBody = (sections: BatchSectionRequest[]) => ({
  sections: sections.map(({ id, descriptor }) => ({
    id,
    [descriptor.kind]: descriptor.query,
  })),
});

const post = (sections: BatchSectionRequest[], signal?: AbortSignal) =>
  fetch(`${API_URL}/analytics-admin/batch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(buildBody(sections)),
    signal,
  });

/**
 * Opens the batch stream and yields frames as they land.
 *
 * A 401 can only happen at the handshake — past that the request is already
 * authorized — so the refresh-and-retry the axios interceptor would have done is
 * only needed there, and only once.
 */
export async function* streamBatch(
  sections: BatchSectionRequest[],
  signal?: AbortSignal
): AsyncGenerator<BatchFrame> {
  let response = await post(sections, signal);

  if (response.status === 401) {
    await refreshAccessToken();
    response = await post(sections, signal);
  }

  if (!response.ok) {
    throw new Error(`Batch request failed with ${response.status}`);
  }

  if (!response.body) {
    throw new Error("Batch response carried no body to read");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffered = "";

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      const split = splitFrames(buffered, decoder.decode(value, { stream: true }));
      buffered = split.rest;

      for (const line of split.lines) {
        yield JSON.parse(line) as BatchFrame;
      }
    }

    // A well-formed stream ends on a newline, so anything left is a frame the
    // connection was cut in the middle of. Dropping it silently would show as a
    // section stuck loading forever.
    if (buffered.trim().length > 0) {
      throw new Error("The batch stream ended mid-frame");
    }
  } finally {
    reader.releaseLock();
  }
}
